import { useEffect, useState } from "react";
import { useParams, useLocation, useSearchParams, Link } from "react-router-dom";
import { CheckCircle2, Package, Phone, Gift, Loader2 } from "lucide-react";
import api, { formatDA } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import { useAuth } from "@/context/AuthContext";
import StatusTracker from "@/components/StatusTracker";

export default function OrderSuccess() {
  const { id } = useParams();
  const location = useLocation();
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { settings } = useSettings();
  const { user } = useAuth();
  const [order, setOrder] = useState(location.state?.order || null);
  const [cardPaid, setCardPaid] = useState(false);

  useEffect(() => { window.scrollTo(0, 0); }, []);

  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;
    let tries = 0;
    const poll = async () => {
      try {
        const { data } = await api.get(`/orders/${id}/payment-status`, { params: { session_id: sessionId } });
        if (cancelled) return;
        if (data.payment_status === "paid") { setCardPaid(true); return; }
        if (++tries < 12) setTimeout(poll, 2500);
      } catch { /* silencieux */ }
    };
    poll();
    return () => { cancelled = true; };
  }, [id, sessionId]);

  const rate = settings.loyalty_points_per_100da || 1;
  const earnedPoints = user && settings.loyalty_enabled && order ? Math.floor((order.subtotal || 0) / 100) * rate : 0;

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-16 text-center animate-fade-up">
      <div className="w-20 h-20 rounded-full bg-mint-100 grid place-items-center mx-auto mb-6">
        <CheckCircle2 className="w-11 h-11 text-mint-600" />
      </div>
      <h1 className="font-display font-extrabold text-2xl sm:text-3xl text-slate-dark" data-testid="order-success-title">Commande confirmée !</h1>
      <p className="text-slate-500 mt-3">Merci pour votre confiance. Nous vous contacterons bientôt pour confirmer la livraison.</p>

      {order && (
        <div className="bg-white rounded-2xl border border-slate-200/80 p-6 mt-8 text-left">
          <div className="flex items-center justify-between pb-4 border-b border-mint-100">
            <div><div className="text-xs text-slate-400 font-mono-label">N° Commande</div><div className="font-semibold">#{id.slice(-8).toUpperCase()}</div></div>
            <span className="px-3 py-1 rounded-full bg-amber-100 text-amber-700 text-xs font-semibold">{order.status}</span>
          </div>
          <div className="py-5 border-b border-mint-100"><StatusTracker status={order.status} /></div>
          <div className="py-4 space-y-2">
            {order.items?.map((it, i) => (
              <div key={i} className="flex justify-between text-sm">
                <span className="text-slate-600">{it.quantity} × {it.name}</span>
                <span className="font-medium">{formatDA(it.price * it.quantity)}</span>
              </div>
            ))}
          </div>
          <div className="border-t border-mint-100 pt-4 space-y-1 text-sm">
            <div className="flex justify-between text-slate-500"><span>Sous-total</span><span>{formatDA(order.subtotal)}</span></div>
            <div className="flex justify-between text-slate-500"><span>Livraison {order.delivery_method ? `(${order.delivery_method})` : `(${order.wilaya})`}</span><span>{order.delivery === 0 ? "Gratuit" : formatDA(order.delivery)}</span></div>
            {order.discount > 0 && <div className="flex justify-between text-mint-700"><span>Remise {order.promo_code ? `(${order.promo_code})` : ""}</span><span>- {formatDA(order.discount)}</span></div>}
            <div className="flex justify-between font-display font-extrabold text-lg"><span>Total</span><span className="text-mint-700">{formatDA(order.total)}</span></div>
            <div className="text-xs text-slate-400 mt-2 flex items-center justify-end gap-1.5">
              Paiement : {order.payment_method === "card"
                ? (cardPaid ? <span className="text-mint-700 font-semibold">Carte bancaire — payée ✓</span> : <span className="inline-flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Carte bancaire — vérification…</span>)
                : order.payment_method === "baridimob" ? "BaridiMob" : "À la livraison"}
            </div>
          </div>
        </div>
      )}

      {earnedPoints > 0 && (
        <div className="bg-mint-50 border border-mint-200 rounded-2xl p-5 mt-6 flex items-center gap-3 text-left" data-testid="loyalty-earned-banner">
          <span className="w-11 h-11 rounded-full bg-mint-600 grid place-items-center shrink-0"><Gift className="w-5 h-5 text-white" /></span>
          <div>
            <div className="font-display font-bold text-mint-800">Bravo ! Vous avez gagné {earnedPoints} points</div>
            <div className="text-sm text-slate-600">Ils seront ajoutés à votre compte fidélité dès la livraison de votre commande.</div>
          </div>
        </div>
      )}

      <div className="flex flex-wrap gap-3 justify-center mt-8">
        <Link to="/catalogue" className="px-6 py-3 rounded-full bg-mint-600 text-white font-semibold hover:bg-mint-700">Continuer mes achats</Link>
        <Link to="/compte" className="px-6 py-3 rounded-full border border-mint-200 font-semibold flex items-center gap-2"><Package className="w-4 h-4" /> Mes commandes</Link>
      </div>
      <a href={`tel:${settings.phone_link}`} className="inline-flex items-center gap-2 mt-6 text-sm text-slate-500"><Phone className="w-4 h-4 text-mint-600" /> Une question ? {settings.phone}</a>
    </div>
  );
}
